/**
 * SQL 튜닝 작업 API (docs/features/20-sql-tuning.md).
 *
 * 수집·검증·저장은 `/sql-tuning`(AI 무관 — AI 제외 빌드에서도 돈다), AI 단계만 `/assistant/sql-tuning`.
 * **작업 조회·삭제·수집·검증에 connectionId 를 싣지 않는다** — 작업이 연결을 들고 있고, 서버 초크포인트가 본문의
 * connectionId 를 보면 접근 검사를 도는데 지워진 연결이면 자기 작업을 못 연다(보관함과 같은 규칙).
 */
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || '/sql-developer';
const ROOT = `${BASE_URL}/sql-tuning`;
const AI_ROOT = `${BASE_URL}/assistant/sql-tuning`;
const enc = encodeURIComponent;
// 실측·검증은 느린 SQL 을 끝까지 읽으므로 길게 — 끊을 때는 「중지」(cancel)를 쓴다
const LONG = { timeout: 30 * 60 * 1000 };

export async function listJobs(q = '') { return (await axios.get(`${ROOT}/jobs`, { params: { q } })).data; }
export async function createJob({ connectionId, title, target }) { return (await axios.post(`${ROOT}/jobs`, { connectionId, title, target })).data; }
export async function getJob(id) { return (await axios.get(`${ROOT}/jobs/${enc(id)}`)).data; }
export async function patchJob(id, patch, { title, allowDraft, dropReasons } = {}) { return (await axios.put(`${ROOT}/jobs/${enc(id)}`, { patch, title, allowDraft, dropReasons })).data; }
export async function deleteJob(id) { return (await axios.delete(`${ROOT}/jobs/${enc(id)}`)).data; }
export async function cancelJob(id) { return (await axios.post(`${ROOT}/jobs/${enc(id)}/cancel`, {})).data; }

/** 1단계 — SQL 목록(GV$SQLAREA). 여기는 새 작업을 만들기 전이라 연결을 본문에 싣는다 */
export async function listSqlArea({ connectionId, q, orderBy, limit }) { return (await axios.post(`${ROOT}/sqlarea`, { connectionId, q, orderBy, limit })).data; }
export async function sqlAreaDetail({ connectionId, sqlId }) { return (await axios.post(`${ROOT}/sqlarea/detail`, { connectionId, sqlId })).data; }

/** 2단계 — parts: { plan: { mode, monitorReport }, stats, profile }, confirmProd */
export async function collectJob(id, parts, { confirmProd } = {}) {
  return (await axios.post(`${ROOT}/jobs/${enc(id)}/collect`, { parts, confirmProd }, LONG)).data;
}
/** 4단계 — 대안 검증(기계). 인덱스 대안은 confirmIndex, 운영 연결은 confirmProd 가 있어야 돈다(없으면 409 + 미리보기) */
export async function verifyAlt(id, altId, { confirmIndex, confirmProd } = {}) {
  return (await axios.post(`${ROOT}/jobs/${enc(id)}/alternatives/${enc(altId)}/verify`, { confirmIndex, confirmProd }, LONG)).data;
}
// view: 'brief' = 축약(주요 진단·개선 방안·개선 결과만), 그 밖은 전체
export async function getReport(id, view) { return (await axios.get(`${ROOT}/jobs/${enc(id)}/report`, { params: view === 'brief' ? { view: 'brief' } : undefined })).data; }

/** 튜닝 가이드 — 이 작업에 실리는 것·고를 수 있는 것·검증 순서(가이드의 처방 순서). AI 와 무관한 경로다 */
export async function getGuide(id) { return (await axios.get(`${ROOT}/jobs/${enc(id)}/guide`)).data; }
/** 분류 코드 목록(증상·원인·처방) — 튜닝 사례와 같은 목록. LLM 을 부르지 않는 경로라 AI 제외 빌드에서도 돈다 */
export async function getTopics() { return (await axios.get(`${BASE_URL}/assistant/tuning-cases/topics`)).data; }

/** 튜닝 사례 — 닮은 사례 찾기 · 작업 결과를 사례 양식으로 · 등록한 사례를 작업에 잇기(LLM 을 부르지 않는다) */
export async function findCases(id) { return (await axios.post(`${AI_ROOT}/jobs/${enc(id)}/cases`, {})).data; }
export async function caseSeed(id) { return (await axios.get(`${AI_ROOT}/jobs/${enc(id)}/case-seed`)).data; }
export async function linkCase(id, caseId) { return (await axios.post(`${AI_ROOT}/jobs/${enc(id)}/case-link`, { caseId })).data; }

/** 개선안 작성 가이드 — 공용 문서(모두 읽고 관리자만 고친다). AI 단계 프롬프트에 늘 실린다 */
export async function getWritingGuide() { return (await axios.get(`${AI_ROOT}/writing-guide`)).data; }
export async function saveWritingGuide(body) { return (await axios.put(`${AI_ROOT}/writing-guide`, { body })).data; }

/** AI 단계 진행 상황(1초마다 읽는다) · 중지 — 내용 없이 단계·LLM·계정 경로·모델·시각·글자 수 */
export async function aiProgress(id) { return (await axios.get(`${AI_ROOT}/jobs/${enc(id)}/ai-progress`)).data; }
export async function aiCancel(id) { return (await axios.post(`${AI_ROOT}/jobs/${enc(id)}/ai-cancel`, {})).data; }

/** AI 단계 — step: diagnose | alternatives | interpret | final. instruction 이 있으면 「지시를 붙여 다시 쓰기」 */
export async function aiStep(id, step, { provider, scope, instruction, replace } = {}) {
  return (await axios.post(`${AI_ROOT}/jobs/${enc(id)}/ai/${enc(step)}`, { provider, scope, instruction, replace }, { timeout: 6 * 60 * 1000 })).data;
}
